import { useEffect, useState, type CSSProperties } from "react";
import { fetchSchools, type SchoolResult } from "@/services/schools";

const MAX_LOGOS = 12;
const COLLAGE_POSITIONS = [
  "-translate-y-1 rotate-[-5deg]",
  "translate-y-2 rotate-[3deg]",
  "-translate-y-2 rotate-[-2deg]",
  "translate-y-1 rotate-[5deg]",
  "-translate-y-1 rotate-[2deg]",
  "translate-y-2 rotate-[-4deg]",
  "-translate-y-2 rotate-[4deg]",
  "translate-y-1 rotate-[-2deg]",
  "-translate-y-1 rotate-[3deg]",
  "translate-y-2 rotate-[-5deg]",
  "-translate-y-2 rotate-[2deg]",
  "translate-y-1 rotate-[4deg]",
];

function uniqueLogoSchools(results: SchoolResult[]): SchoolResult[] {
  const seen = new Set<string>();
  return results.filter(school => {
    if (!school.logoUrl || seen.has(school.logoUrl)) return false;
    seen.add(school.logoUrl);
    return true;
  });
}

interface DreamSchoolLogoCollageProps {
  gender?: string | null;
}

/** A decorative wall of real program marks to make the dream-school prompt feel tangible. */
export function DreamSchoolLogoCollage({ gender }: DreamSchoolLogoCollageProps) {
  const [schools, setSchools] = useState<SchoolResult[] | null>(null);

  useEffect(() => {
    let active = true;

    fetchSchools({
      groupByCollege: true,
      preferGender: gender ?? undefined,
      limit: 40,
    })
      .then(response => {
        if (active) setSchools(uniqueLogoSchools(response.results).slice(0, MAX_LOGOS));
      })
      .catch(() => {
        if (active) setSchools([]);
      });

    return () => {
      active = false;
    };
  }, [gender]);

  if (schools?.length === 0) return null;

  const items = schools ?? Array.from({ length: 10 }, () => null);

  return (
    <div
      data-testid="dream-school-logo-collage"
      className="relative mb-7 min-h-[132px] overflow-hidden rounded-[26px] border border-border-strong bg-[linear-gradient(145deg,#f6faf3_0%,#e4f0df_52%,#d8e9d5_100%)] px-4 py-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_12px_32px_rgba(25,58,43,0.08)] sm:min-h-[146px] sm:px-6"
      aria-hidden="true"
    >
      <div className="pointer-events-none absolute -left-20 top-1/2 h-32 w-[130%] -translate-y-1/2 rotate-[-4deg] rounded-[50%] border border-white/70 bg-white/20" />
      <div className="pointer-events-none absolute -right-20 -top-24 h-48 w-48 rounded-full bg-amber/25 blur-2xl" />

      <div className="relative z-10 flex flex-wrap items-center justify-center gap-2.5 sm:gap-3">
        {items.map((school, index) => {
          const primary = school?.brandColorPrimary || "#789b78";
          const plaqueStyle = {
            "--plaque-color": primary,
            boxShadow: `0 0 0 2px ${primary}33, 0 10px 20px rgba(25, 58, 43, 0.12)`,
          } as CSSProperties;

          return (
            <div
              key={school?.schoolId || `school-logo-placeholder-${index}`}
              data-school-name={school?.schoolName}
              style={school ? plaqueStyle : undefined}
              className={`${COLLAGE_POSITIONS[index]} grid h-12 w-12 place-items-center rounded-[14px] border border-white/90 bg-white p-1.5 sm:h-14 sm:w-14 sm:rounded-[17px] sm:p-2 ${index > 7 ? "hidden sm:grid" : "grid"} ${school ? "" : "animate-pulse bg-white/55 shadow-none"}`}
            >
              {school?.logoUrl && (
                <img
                  src={school.logoUrl}
                  alt=""
                  draggable={false}
                  className="h-full w-full object-contain"
                />
              )}
            </div>
          );
        })}
      </div>

      <p className="relative z-10 mt-4 text-center font-mono text-[11px] font-bold uppercase tracking-[0.13em] text-muted-foreground">
        Your list can start anywhere
      </p>
    </div>
  );
}
